import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Rol = { id: number; nombre: string };
type Usuario = {
  id: number; nombre: string; email: string; estado: string;
  dosFactorHabilitado: boolean; ultimoLogin: string | null; roles: Rol[];
};

const ESTADOS = ["activo", "inactivo", "bloqueado"];

function estadoBadge(estado: string) {
  if (estado === "activo") return "bg-accent-light text-accent-dark";
  if (estado === "bloqueado") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-500";
}

export function Usuarios() {
  const { tienePermiso, sesion } = useAuth();
  const puedeCrear = tienePermiso("core.usuarios:crear");
  const puedeEditar = tienePermiso("core.usuarios:editar");
  const puedeEliminar = tienePermiso("core.usuarios:eliminar");

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [roles, setRoles] = useState<Rol[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<Usuario | null>(null);

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [estado, setEstado] = useState("activo");
  const [rolIds, setRolIds] = useState<number[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [u, r] = await Promise.all([api.get("/usuarios"), api.get("/roles")]);
      setUsuarios(u);
      setRoles(r.map((x: any) => ({ id: x.id, nombre: x.nombre })));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => {
    setEditando(null);
    setNombre(""); setEmail(""); setPassword(""); setEstado("activo"); setRolIds([]);
    setFormError(null);
    setModalAbierto(true);
  };

  const abrirEditar = (u: Usuario) => {
    setEditando(u);
    setNombre(u.nombre); setEmail(u.email); setPassword(""); setEstado(u.estado);
    setRolIds(u.roles.map((r) => r.id));
    setFormError(null);
    setModalAbierto(true);
  };

  const toggleRol = (id: number) => {
    setRolIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      if (editando) {
        const body: any = { nombre, estado, rolIds };
        if (password) body.password = password;
        await api.put(`/usuarios/${editando.id}`, body);
      } else {
        await api.post("/usuarios", { nombre, email, password, rolIds });
      }
      setModalAbierto(false);
      await cargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo guardar el usuario.");
    } finally {
      setGuardando(false);
    }
  };

  const desactivar = async (u: Usuario) => {
    if (!window.confirm(`¿Desactivar a ${u.nombre}? Podrá reactivarse luego cambiando su estado.`)) return;
    try {
      await api.del(`/usuarios/${u.id}`);
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo desactivar el usuario.");
    }
  };

  return (
    <Shell titulo="Usuarios" breadcrumb="Core / Usuarios">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-textmuted">Usuarios con acceso a {sesion?.empresa?.nombre}.</p>
        {puedeCrear && (
          <button className="btn-primary" onClick={abrirNuevo}>+ Nuevo usuario</button>
        )}
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <div className="card overflow-hidden">
        {cargando ? (
          <p className="p-6 text-sm text-textmuted">Cargando...</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-borderc">
                <th className="table-th">Nombre</th>
                <th className="table-th">Email</th>
                <th className="table-th">Roles</th>
                <th className="table-th">2FA</th>
                <th className="table-th">Estado</th>
                <th className="table-th">Último acceso</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id} className="border-b border-borderc last:border-0">
                  <td className="table-td font-medium text-slate-800">{u.nombre}</td>
                  <td className="table-td">{u.email}</td>
                  <td className="table-td">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <span key={r.id} className="badge bg-navy/10 text-navy">{r.nombre}</span>
                      ))}
                    </div>
                  </td>
                  <td className="table-td">{u.dosFactorHabilitado ? "Activo" : "—"}</td>
                  <td className="table-td"><span className={`badge ${estadoBadge(u.estado)}`}>{u.estado}</span></td>
                  <td className="table-td">{u.ultimoLogin || "Nunca"}</td>
                  <td className="table-td text-right space-x-2">
                    {puedeEditar && <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => abrirEditar(u)}>Editar</button>}
                    {puedeEliminar && u.estado === "activo" && (
                      <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => desactivar(u)}>Desactivar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={guardar} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">{editando ? "Editar usuario" : "Nuevo usuario"}</h3>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre completo</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
              <input className="input-field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!editando} required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {editando ? "Nueva contraseña (opcional)" : "Contraseña"}
              </label>
              <input className="input-field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" required={!editando} />
            </div>
            {editando && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Estado</label>
                <select className="input-field" value={estado} onChange={(e) => setEstado(e.target.value)}>
                  {ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Roles</label>
              <div className="flex flex-wrap gap-2">
                {roles.map((r) => (
                  <label key={r.id} className={`badge cursor-pointer border ${rolIds.includes(r.id) ? "bg-accent text-white border-accent" : "bg-white border-borderc text-slate-600"}`}>
                    <input type="checkbox" className="hidden" checked={rolIds.includes(r.id)} onChange={() => toggleRol(r.id)} />
                    {r.nombre}
                  </label>
                ))}
              </div>
            </div>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Guardar"}</button>
            </div>
          </form>
        </div>
      )}
    </Shell>
  );
}
